// Talking to the backend over HTTP. The live roster has its own socket
// (hooks/useLiveRoster); this is for the request/response calls, today just login.

const LOGIN_TIMEOUT_MS = 15000

/**
 * Where the HTTP API lives. The login endpoint is on the same server as the
 * WebSocket, so by default this is worked out from REACT_APP_TALLY_WS_URL
 * (`ws://host:8090/ws` -> `http://host:8090`). REACT_APP_API_BASE_URL overrides it
 * if the two ever move apart. Empty string means "not configured".
 */
export function apiBaseUrl() {
  const explicit = process.env.REACT_APP_API_BASE_URL
  if (explicit) return explicit.replace(/\/+$/, '')

  const wsUrl = process.env.REACT_APP_TALLY_WS_URL
  if (!wsUrl) return ''
  try {
    const { protocol, host } = new URL(wsUrl)
    return `${protocol === 'wss:' ? 'https' : 'http'}://${host}`
  } catch {
    return ''
  }
}

/**
 * Every way a login can fail, as one error the page can switch on.
 *
 *   config      no backend address is set
 *   network     the request never got an answer (server down, wrong address, CORS)
 *   timeout     the server did not answer in time
 *   validation  400 — the server said a field is missing
 *   invalid     401 — wrong email or password
 *   throttled   429 — too many attempts
 *   server      5xx — the server itself failed
 *   unknown     anything else, including a 200 that is not a valid login reply
 */
export class LoginError extends Error {
  constructor(kind, message, status = null) {
    super(message)
    this.name = 'LoginError'
    this.kind = kind
    this.status = status
  }
}

async function readJson(response) {
  try {
    return await response.json()
  } catch {
    // Proxies and crashed servers answer with HTML or nothing at all.
    return null
  }
}

/**
 * POST /api/login. Resolves with `{ token, expiresIn, user }` only when the
 * server says the login worked; every other outcome throws a LoginError.
 */
export async function login({ email_id, password }, { baseUrl = apiBaseUrl() } = {}) {
  if (!baseUrl) {
    throw new LoginError('config', 'The server address is not configured.')
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS)

  let response
  try {
    response = await fetch(`${baseUrl}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email_id, password }),
      signal: controller.signal,
    })
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new LoginError('timeout', 'The server took too long to respond.')
    }
    throw new LoginError('network', 'Could not reach the server.')
  } finally {
    clearTimeout(timer)
  }

  const body = await readJson(response)
  const serverMessage = typeof body?.message === 'string' ? body.message : ''

  if (response.ok) {
    if (body?.success && body.token) {
      return { token: body.token, expiresIn: body.expiresIn, user: body.user ?? null }
    }
    throw new LoginError('unknown', serverMessage || 'The server sent an unexpected reply.', response.status)
  }

  if (response.status === 400) {
    throw new LoginError('validation', serverMessage || 'Email and password are required.', 400)
  }
  if (response.status === 401) {
    throw new LoginError('invalid', serverMessage || 'Invalid email or password.', 401)
  }
  if (response.status === 429) {
    throw new LoginError('throttled', serverMessage || 'Too many attempts. Try again shortly.', 429)
  }
  if (response.status >= 500) {
    throw new LoginError('server', serverMessage || 'The server ran into a problem.', response.status)
  }
  throw new LoginError('unknown', serverMessage || `Unexpected response (${response.status}).`, response.status)
}
